import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { MODULE_ICONS } from "@/lib/serviceModules";
import { UtensilsCrossed } from "lucide-react";

const STATUS_TINT = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  preparing: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20",
  ready: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  picked_up: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  out_for_delivery: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  delivered: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  in_progress: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  cancelled: "bg-red-500/10 text-red-600 border-red-500/20",
};

export default function Orders() {
  const { t, lang } = useLang();
  const { formatPrice } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/orders/mine").then(r => setOrders(r.data));
    api.get("/bookings/mine").then(r => setBookings(r.data)).catch(() => setBookings([]));
  }, []);

  // Combine + sort by created_at desc
  const combined = [
    ...orders.map(o => ({
      key: `o-${o.id}`, id: o.id, kind: "order", module_key: "restaurants",
      link: `/app/orders/${o.id}`, name: o.restaurant_name, image: o.restaurant_image,
      items: o.items?.length || 0, total: o.total, status: o.status, created_at: o.created_at,
    })),
    ...bookings.map(b => ({
      key: `b-${b.id}`, id: b.id, kind: "booking", module_key: b.module_key,
      link: `/app/orders`, name: b.provider_name, image: b.provider_image,
      items: b.items?.length || 0, total: b.total, status: b.status, created_at: b.created_at,
    })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filtered = tab === "all" ? combined : tab === "food" ? combined.filter(x => x.kind === "order") : combined.filter(x => x.kind === "booking");

  return (
    <CustomerLayout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold">{t("yourOrders")}</h1>
        <div className="mt-4 flex gap-2" data-testid="orders-tabs">
          {[
            { k: "all", en: "All", ar: "الكل" },
            { k: "food", en: "Food", ar: "طعام" },
            { k: "services", en: "Services", ar: "خدمات" },
          ].map(x => (
            <button
              key={x.k}
              onClick={() => setTab(x.k)}
              data-testid={`orders-tab-${x.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${tab === x.k ? "bg-[var(--so-orange)] text-white border-[var(--so-orange)]" : "text-[var(--so-text-soft)] border-[var(--so-border)]"}`}
            >
              {lang === "ar" ? x.ar : x.en}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-[var(--so-text-soft)]" data-testid="no-orders">{t("noOrders")}</div>
        ) : (
          <div className="mt-4 space-y-3">
            {filtered.map((o) => {
              const Icon = MODULE_ICONS[o.module_key] || UtensilsCrossed;
              const content = (
                <>
                  {o.image ? (
                    <img src={o.image} alt={o.name} className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[var(--so-orange)]/10 text-[var(--so-orange)] flex items-center justify-center"><Icon className="w-6 h-6" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-bold truncate">{o.name}</div>
                      {o.kind === "booking" && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--so-border)] text-[var(--so-text-soft)]">
                          {o.module_key.replace("_", " ")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--so-text-soft)]">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="mt-1 text-sm">{o.items} item(s) · <span className="font-bold text-[var(--so-orange)]">{formatPrice(o.total)}</span></div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${STATUS_TINT[o.status] || ""}`}>{o.status.replace("_", " ")}</span>
                </>
              );
              return o.kind === "order" ? (
                <Link key={o.key} to={o.link} data-testid={`order-${o.id}`} className="so-card p-4 flex items-center gap-3">{content}</Link>
              ) : (
                <div key={o.key} data-testid={`booking-${o.id}`} className="so-card p-4 flex items-center gap-3">{content}</div>
              );
            })}
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}
